//
// # Move-Pipeline Helpers
//
// Pure functions shared by every "move an item" mode handler — MoveBase
// (single-target), MoveSelection (multi-target), and MoveSegments
// (per-segment). Each handler reduces to the same pipeline:
//
//   rawDelta    = currentMouse - initialMouse        // captures cursor intent
//   constrained = maybeConstrainToAxis(rawDelta, shift)
//   target      = snap(origLoc + constrained)        // absolute form
//      OR
//   delta       = snap(constrained)                  // delta form; caller adds
//                                                    // to per-entity origLoc
//      OR
//   delta       = snap(origLoc + constrained) - origLoc  // single-entity delta
//
// Extracting these as pure helpers lets us unit-test the move math without
// instantiating the full Editor (which would require canvas/DOM/change-log
// mocks).
//
// The forms differ only in whether grid-snap hits the absolute location or the
// delta. For an origLoc already on the grid they agree; we keep all three so
// each caller can use the shape that matches its plumbing.
//

import { Point } from "GenAlphaModel";

// Minimum cursor travel, in browser/page pixels, before a click-drag is treated
// as an actual drag. A mouse press-and-release whose cursor stays within this
// radius is a plain click (i.e. select), and the mode leaves geometry
// untouched. Measured in page pixels so the threshold is independent of zoom.
// Tune here — it is shared by every click-to-drag mode handler.
export const DRAG_START_THRESHOLD_PX = 4;

// Small latch shared by click-to-drag mode handlers. It suppresses movement
// until the cursor has travelled past DRAG_START_THRESHOLD_PX from the initial
// mouse-press, so the involuntary sub-pixel motion of an ordinary click doesn't
// nudge (or, for wired instances, reroute) anything.
//
// Keyboard-initiated grabs and entity placement have no meaningful press point
// and must track the cursor immediately: construct with `null` (or use
// `DragThreshold.passthrough()`) and the latch reports "begun" from the start.
export class DragThreshold {
  // Once the drag has begun it stays begun for the mode's lifetime.
  private begun_: boolean;

  constructor(private readonly initialPage: Point | null) {
    // With no press point (keyboard grab / placement) there is nothing to wait
    // for — the drag is considered begun immediately.
    this.begun_ = initialPage === null;
  }

  // Did this come from a mouse press? False for keyboard grabs and placement,
  // which have no press point to measure from. Lets a mode tell a drag
  // (commits on release) from a grab (commits on the next click). Named to
  // match `MoveSelection.enter`'s parameter of the same meaning.
  get fromMousePress(): boolean {
    return this.initialPage !== null;
  }

  // A latch that never suppresses — for keyboard- or placement-initiated modes.
  static passthrough(): DragThreshold {
    return new DragThreshold(null);
  }

  // True once the drag has begun (the threshold was crossed on this or an
  // earlier call). Latches on: once true it stays true. `currentPage` is the
  // live cursor position in browser/page pixels.
  begun(currentPage: Point): boolean {
    if (this.begun_) return true;
    if (
      this.initialPage !== null &&
      Point.dist(currentPage, this.initialPage) > DRAG_START_THRESHOLD_PX
    ) {
      this.begun_ = true;
    }
    return this.begun_;
  }
}

// If shift is held, zero out the smaller-magnitude axis of `rawDelta`.
// Otherwise return `rawDelta` unchanged. Tie-break (|dx| === |dy|) favors X
// (matches Figma / Illustrator).
//
// Operates on the *raw* (pre-snap) delta so that near the origin — where the
// snapped delta would be (0, 0) and carry no information — the cursor's
// intent is captured immediately.
export function maybeConstrainToAxis(rawDelta: Point, shift: boolean): Point {
  if (!shift) return rawDelta;
  return Math.abs(rawDelta.x) >= Math.abs(rawDelta.y)
    ? Point.new(rawDelta.x, 0)
    : Point.new(0, rawDelta.y);
}

// Compute the new (absolute) location of an item being moved, snapping the
// absolute position. Used by handlers that move a single target whose origLoc
// is the natural anchor (MoveBase).
export function computeMovedLoc(args: {
  origLoc: Point;
  initialMousePos: Point;
  currentMousePos: Point;
  shift: boolean;
  snap: (p: Point) => Point;
}): Point {
  const { origLoc, initialMousePos, currentMousePos, shift, snap } = args;
  const rawDelta = Point.sub(currentMousePos, initialMousePos);
  const constrained = maybeConstrainToAxis(rawDelta, shift);
  return snap(Point.add(origLoc, constrained));
}

// Delta form of `computeMovedLoc`, for handlers that apply an offset but move
// only one entity. On-grid it returns the same delta as `computeMovedDelta`;
// off-grid the delta also re-seats the entity onto the grid.
export function computeMovedToGrid(args: {
  origLoc: Point;
  initialMousePos: Point;
  currentMousePos: Point;
  shift: boolean;
  snap: (p: Point) => Point;
}): Point {
  return Point.sub(computeMovedLoc(args), args.origLoc);
}

// Compute a *grid-snapped delta* to apply to each entity in a group move.
// Used by handlers that move multiple targets and need each entity to move
// by the same snapped offset (MoveSelection). Snapping the delta keeps the
// group's spacing intact, at the cost of leaving an off-grid member off-grid.
//
// Callers add the returned delta to each entity's origLoc.
export function computeMovedDelta(args: {
  initialMousePos: Point;
  currentMousePos: Point;
  shift: boolean;
  snap: (p: Point) => Point;
}): Point {
  const { initialMousePos, currentMousePos, shift, snap } = args;
  const rawDelta = Point.sub(currentMousePos, initialMousePos);
  const constrained = maybeConstrainToAxis(rawDelta, shift);
  return snap(constrained);
}
